import React, { useState } from "react";
import { useExpensesInRange, useCreateExpense, useUpdateExpense, useDeleteExpense } from "../../../hooks/expense";
import { useCategoriesQuery } from "../../../hooks/category";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  TableContainer,
  Skeleton,
  useTheme,
  Fade,
  Avatar,
} from "@mui/material";
import {
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import Expense from "../../../models/finance/Expense";
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMonthIndex,
} from "../../../utils/DateUtil";
import ExpenseFormDrawer from "../ExpenseFormDrawer";
import { CategoryType } from "../../../models/finance/CategoryType";
import { useCurrencyFormatter } from "../../../contexts/CurrencyContext";
import { GradientButton } from "../../common/StyledComponents";
import { useError } from "../../../error/useError";
import ErrorAlert from "../../common/ErrorAlert";
import { useUserProfileContext } from "../../../contexts/ProfileContext";

interface ExpenseTableComponentProps {
  year: number;
  month: string;
  onExpenseChange?: () => void;
}

const ExpenseTableComponent: React.FC<ExpenseTableComponentProps> = ({
  year,
  month,
  onExpenseChange,
}) => {
  const theme = useTheme();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();

  const { error, helpText, clearError } = useError();
  
  const startDate = getFirstDayOfMonth(year, getMonthIndex(month));
  const endDate = getLastDayOfMonth(year, getMonthIndex(month));
  
  const { data: expenses = [], isLoading: loading, refetch: refetchExpenses } = useExpensesInRange(startDate, endDate);
  const { data: categories = [] } = useCategoriesQuery(CategoryType.Expense, true);
  const { mutate: deleteExpense } = useDeleteExpense();
  const { mutate: createExpense } = useCreateExpense();
  const { mutate: updateExpense } = useUpdateExpense();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc"
  );
  const [sortBy, setSortBy] = useState<"date" | "amount" | "category">(
    (searchParams.get("sortBy") as "date" | "amount" | "category") || "date"
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get("category") || ""
  );

  // Filter menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Drawer logic
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [selectedExpense, setSelectedExpense] = useState<Expense | undefined>(undefined);

  // Rest of your existing logic for sorting, filtering, etc.
  const updateSearchParams = (newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  };

  const handleSort = (property: "date" | "amount" | "category") => {
    const isAsc = sortBy === property && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setSortBy(property);
    setSortOrder(newOrder);
    updateSearchParams({ sortBy: property, sortOrder: newOrder });
  };

  const handleCategoryFilter = (categoryTitle: string) => {
    setSelectedCategory(categoryTitle);
    setAnchorEl(null);
    updateSearchParams({ category: categoryTitle });
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("");
    setAnchorEl(null);
    updateSearchParams({ category: "" });
  };

  // Filter and sort expenses
  const filteredExpenses = expenses.filter((expense) =>
    selectedCategory ? expense.category === selectedCategory : true
  );

  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "date":
        comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        break;
      case "amount":
        comparison = a.amount - b.amount;
        break;
      case "category":
        comparison = a.category.localeCompare(b.category);
        break;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const paginatedExpenses = sortedExpenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const handleAddExpense = () => {
    setSelectedExpense(undefined);
    setDrawerMode("add");
    setDrawerOpen(true);
  };

  const handleSaveExpense = (
    id: string,
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => {
    if (drawerMode === "add") {
      createExpense(
        { date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            onExpenseChange?.();
          }
        }
      );
    } else {
      updateExpense(
        { id, date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            onExpenseChange?.();
          }
        }
      );
    }
  };

  const handleSaveAndAddAnother = (
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => {
    if (drawerMode === "add") {
      createExpense(
        { date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(true);
            onExpenseChange?.();
          }
        }
      );
    }
  }

  const handleDeleteExpense = (expense: Expense) => {
    deleteExpense(
      { id: expense.id },
      {
        onSuccess: () => {
          setDrawerOpen(false);
          onExpenseChange?.();
        }
      }
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="rectangular" width={200} height={40} />
          <Skeleton variant="rectangular" width={120} height={36} />
        </Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorAlert
          error={error}
          helpText={helpText}
          onClear={clearError}
          onRetry={() => refetchExpenses()}
          title="Expense Table Error"
        />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={400}>
      <Box>
        {/* Header with stats and actions */}
        <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: 2, 
            mb: 2 
          }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Expenses for {month} {year}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${filteredExpenses.length} transactions`}
                  variant="outlined"
                  size="small"
                />
                <Chip 
                  label={`Total: ${formatCurrency(totalAmount)}`}
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
            
            <GradientButton
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
              size="small"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Add Expense
            </GradientButton>
          </Box>

          {/* Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Category Filter
              {selectedCategory && (
                <Chip 
                  label={selectedCategory} 
                  size="small" 
                  onDelete={clearCategoryFilter}
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </Button>
          </Box>
        </Box>

        {/* Table */}
        {filteredExpenses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
              }}
            >
              <ReceiptIcon sx={{ fontSize: '2rem' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No expenses found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedCategory 
                ? `No expenses found for category "${selectedCategory}"`
                : `No expenses recorded for ${month} ${year}`
              }
            </Typography>
            <GradientButton
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
            >
              Add Your First Expense
            </GradientButton>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 650, sm: 'auto' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "date"}
                        direction={sortBy === "date" ? sortOrder : "asc"}
                        onClick={() => handleSort("date")}
                        sx={{ fontWeight: 600 }}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "category"}
                        direction={sortBy === "category" ? sortOrder : "asc"}
                        onClick={() => handleSort("category")}
                        sx={{ fontWeight: 600 }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === "amount"}
                        direction={sortBy === "amount" ? sortOrder : "asc"}
                        onClick={() => handleSort("amount")}
                        sx={{ fontWeight: 600 }}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedExpenses.map((expense, index) => (
                    <TableRow 
                      key={expense.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:nth-of-type(odd)': {
                          backgroundColor: theme.palette.action.selected + '30',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {formatDate(expense.date)}
                      </TableCell>
                      <TableCell>{expense.description}</TableCell>
                      <TableCell>
                        <Chip 
                          label={expense.category}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit expense">
                            <IconButton
                              size="small"
                              onClick={() => handleEditExpense(expense)}
                              sx={{ 
                                color: theme.palette.info.main,
                                '&:hover': { backgroundColor: theme.palette.info.light + '20' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete expense">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteExpense(expense)}
                              sx={{ 
                                color: theme.palette.error.main,
                                '&:hover': { backgroundColor: theme.palette.error.light + '20' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sortedExpenses.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            />
          </>
        )}

        {/* Category filter menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: { maxHeight: 300, minWidth: 200 }
          }}
        >
          <MenuItem onClick={clearCategoryFilter}>
            <Typography sx={{ fontStyle: 'italic' }}>All Categories</Typography>
          </MenuItem>
          {categories.map((category) => (
            <MenuItem
              key={category.id}
              onClick={() => handleCategoryFilter(category.title)}
              selected={selectedCategory === category.title}
            >
              {category.title}
            </MenuItem>
          ))}
        </Menu>

        {/* Expense form drawer */}
        <ExpenseFormDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSave={handleSaveExpense}
          onSaveAndAdd={handleSaveAndAddAnother}
          onDelete={handleDeleteExpense}
          mode={drawerMode}
          initialData={selectedExpense}
          selectedYear={year}
          selectedMonth={getMonthIndex(month)}
          categories={categories}
        />
      </Box>
    </Fade>
  );
};

export default ExpenseTableComponent;
